import type { FormEvent } from 'react';
import { Icon } from '../Icon';
import { EVAL_GROUPS, evalGroupLabel } from '../../lib/taskProcess';
import { QUARTERS, type YearSeasonLock } from '../../lib/seasonLock';
import type { Member, OrgTeam } from '../../types';

interface AdminTabProps {
    evalYear: number;
    seasonLock: YearSeasonLock;
    handleSaveSeasonLock: (next: YearSeasonLock) => void;
    publicRegistrationOpen: boolean;
    handleSetPublicRegistration: (open: boolean) => void;
    departmentMembers: Member[];
    orgTeamList: OrgTeam[];
    handleRunDbAudit: () => void;
    handleExportToExcel: () => void;
    handleAddDepartment: (e: FormEvent) => void;
    newDeptOrg: string;
    setNewDeptOrg: (v: string) => void;
    newDeptTeam: string;
    setNewDeptTeam: (v: string) => void;
    handleAddMember: (e: FormEvent) => void;
    newMemberName: string;
    setNewMemberName: (v: string) => void;
    adminMemberOrg: string;
    setAdminMemberOrg: (v: string) => void;
    adminMemberTeam: string;
    setAdminMemberTeam: (v: string) => void;
    adminMemberDept: string;
    setAdminMemberDept: (v: string) => void;
    newMemberDuty: string;
    setNewMemberDuty: (v: string) => void;
    newMemberEvalGroup: string;
    setNewMemberEvalGroup: (v: string) => void;
    adminInitPassword: string;
    setAdminInitPassword: (v: string) => void;
    newMemberPerf: string;
    setNewMemberPerf: (v: string) => void;
    newMemberBehav: string;
    setNewMemberBehav: (v: string) => void;
    newMemberNote: string;
    setNewMemberNote: (v: string) => void;
    handleDeleteMember: (memberId: string, name: string) => void;
    handleSetMemberDisabled: (memberId: string, name: string, disabled: boolean) => void;
}

export function AdminTab({
    evalYear,
    seasonLock,
    handleSaveSeasonLock,
    publicRegistrationOpen,
    handleSetPublicRegistration,
    departmentMembers,
    orgTeamList,
    handleRunDbAudit,
    handleExportToExcel,
    handleAddDepartment,
    newDeptOrg,
    setNewDeptOrg,
    newDeptTeam,
    setNewDeptTeam,
    handleAddMember,
    newMemberName,
    setNewMemberName,
    adminMemberOrg,
    setAdminMemberOrg,
    adminMemberTeam,
    setAdminMemberTeam,
    adminMemberDept,
    setAdminMemberDept,
    newMemberDuty,
    setNewMemberDuty,
    newMemberEvalGroup,
    setNewMemberEvalGroup,
    adminInitPassword,
    setAdminInitPassword,
    newMemberPerf,
    setNewMemberPerf,
    newMemberBehav,
    setNewMemberBehav,
    newMemberNote,
    setNewMemberNote,
    handleDeleteMember,
    handleSetMemberDisabled,
}: AdminTabProps) {
    return (
        <div className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div>
                    <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                        <Icon name="lock" className="w-5 h-5 text-indigo-600" /> {evalYear}년 시즌 잠금
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                        마감 후 일반·파트장 수정을 막고, HR만 예외로 열 수 있습니다. (P3-0 #2)
                    </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {QUARTERS.map((q) => (
                        <label key={q} className="flex items-center gap-2 text-sm border border-slate-200 rounded-xl px-3 py-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={Boolean(seasonLock.quarters[q])}
                                onChange={(e) =>
                                    handleSaveSeasonLock({
                                        ...seasonLock,
                                        quarters: { ...seasonLock.quarters, [q]: e.target.checked },
                                    })
                                }
                            />
                            <span className="font-semibold">{q} 잠금</span>
                        </label>
                    ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {([
                        ['peerEvalLocked', '다면평가 잠금'],
                        ['taskRegisterLocked', '과제 등록 잠금'],
                        ['calibrationLocked', 'Calibration 잠금'],
                    ] as const).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-sm border border-slate-200 rounded-xl px-3 py-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={Boolean(seasonLock[key])}
                                onChange={(e) =>
                                    handleSaveSeasonLock({
                                        ...seasonLock,
                                        [key]: e.target.checked,
                                    })
                                }
                            />
                            <span className="font-semibold">{label}</span>
                        </label>
                    ))}
                </div>
                <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-xl px-3 py-2 cursor-pointer max-w-md">
                    <input
                        type="checkbox"
                        checked={publicRegistrationOpen}
                        onChange={(e) => handleSetPublicRegistration(e.target.checked)}
                    />
                    <span className="font-semibold">로그인 화면 공개 가입 허용</span>
                </label>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                <div>
                    <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                        <Icon name="building" className="w-5 h-5 text-indigo-600" /> 커스텀 부서 및 인력 연동 관리
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">부서 및 인원을 제한 없이 가변 입력하고, 합의된 결과물을 보상 시스템 연동을 위해 엑셀로 내보냅니다.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <button
                        onClick={handleRunDbAudit}
                        className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl text-xs font-bold shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                        <Icon name="shield" className="w-4 h-4" /> DB 무결성 점검
                    </button>
                    <button
                        onClick={handleExportToExcel}
                        className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-xl text-xs font-bold shadow-lg transition-all flex items-center justify-center gap-2"
                    >
                        <Icon name="download" className="w-4 h-4" /> 엑셀 데이터 파일 다운로드 (XLSX)
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    {/* 부서 추가 폼 */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <h4 className="font-bold text-sm text-slate-900 mb-3 flex items-center gap-1.5">
                            <Icon name="plus-circle" className="text-indigo-600" /> 신규 부서 등록 포맷
                        </h4>
                        <form onSubmit={handleAddDepartment} className="space-y-3">
                            <input
                                type="text"
                                value={newDeptOrg}
                                onChange={(e) => setNewDeptOrg(e.target.value)}
                                placeholder="상위 조직명 (예: 사업지원담당)"
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800"
                            />
                            <input
                                type="text"
                                value={newDeptTeam}
                                onChange={(e) => setNewDeptTeam(e.target.value)}
                                placeholder="하위 팀명 (예: IT융합연구팀)"
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800"
                            />
                            <button
                                type="submit"
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-xl text-xs font-bold transition-all"
                            >
                                부서 포맷 등록
                            </button>
                        </form>
                    </div>

                    {/* 임직원 직접 등록 폼 */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <h4 className="font-bold text-sm text-slate-900 mb-4 flex items-center gap-1.5">
                            <Icon name="user-check" className="text-indigo-600" /> 임직원 신규 생성 및 배정
                        </h4>
                        <form onSubmit={handleAddMember} className="space-y-4 text-xs">
                            <div>
                                <label className="block text-slate-700 font-bold mb-1">성명</label>
                                <input
                                    type="text"
                                    required
                                    value={newMemberName}
                                    onChange={(e) => setNewMemberName(e.target.value)}
                                    placeholder="예: 홍길동"
                                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-700 font-bold mb-1">소속 조직</label>
                                    <select
                                        value={adminMemberOrg}
                                        onChange={(e) => {
                                            const org = e.target.value;
                                            setAdminMemberOrg(org);
                                            const firstTeam = orgTeamList.find(d => d.org === org);
                                            const team = firstTeam ? firstTeam.team : '';
                                            setAdminMemberTeam(team);
                                            setAdminMemberDept(team ? `${org} ${team}` : '');
                                        }}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800 cursor-pointer"
                                    >
                                        {[...new Set(orgTeamList.map(d => d.org))].map(org => (
                                            <option key={org} value={org}>{org}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-700 font-bold mb-1">소속 팀</label>
                                    <select
                                        value={adminMemberTeam}
                                        onChange={(e) => {
                                            const team = e.target.value;
                                            setAdminMemberTeam(team);
                                            setAdminMemberDept(adminMemberOrg ? `${adminMemberOrg} ${team}` : '');
                                        }}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800 cursor-pointer"
                                    >
                                        {orgTeamList.filter(d => d.org === adminMemberOrg).map(d => (
                                            <option key={d.team} value={d.team}>{d.team}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-700 font-bold mb-1">직책</label>
                                    <select
                                        value={newMemberDuty}
                                        onChange={(e) => setNewMemberDuty(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800 cursor-pointer"
                                    >
                                        <option value="팀원">팀원</option>
                                        <option value="파트장">파트장</option>
                                        <option value="팀장">팀장</option>
                                        <option value="담당자">담당자 (HR 관리자 권한)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-700 font-bold mb-1">평가군 (Category)</label>
                                    <select
                                        value={newMemberEvalGroup}
                                        onChange={(e) => setNewMemberEvalGroup(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800 cursor-pointer"
                                    >
                                        {EVAL_GROUPS.map(g => (
                                            <option key={g.value || 'none'} value={g.value}>{g.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-slate-700 font-bold mb-1">초기 비밀번호</label>
                                <input
                                    type="text"
                                    required
                                    value={adminInitPassword}
                                    onChange={(e) => setAdminInitPassword(e.target.value)}
                                    placeholder="8자 이상"
                                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-700 font-bold mb-1">1차 성과 등급</label>
                                    <select
                                        value={newMemberPerf}
                                        onChange={(e) => setNewMemberPerf(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800 cursor-pointer"
                                    >
                                        <option value="EX">훌륭해요 (EX)</option>
                                        <option value="GD">준수해요 (GD)</option>
                                        <option value="NI">노력해요 (NI)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-700 font-bold mb-1">1차 행동 등급</label>
                                    <select
                                        value={newMemberBehav}
                                        onChange={(e) => setNewMemberBehav(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-slate-800 cursor-pointer"
                                    >
                                        <option value="EX">훌륭해요 (EX)</option>
                                        <option value="GD">준수해요 (GD)</option>
                                        <option value="NI">노력해요 (NI)</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-slate-700 font-bold mb-1">특이사항 코멘트</label>
                                <textarea
                                    value={newMemberNote}
                                    onChange={(e) => setNewMemberNote(e.target.value)}
                                    placeholder="연초 실적 및 특이 성과 내역 기술"
                                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2.5 h-16 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold leading-relaxed text-slate-800"
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl text-xs font-bold shadow transition-all"
                            >
                                임직원 신규 생성
                            </button>
                        </form>
                    </div>
                </div>

                {/* 임직원 마스터 현황 */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                            <Icon name="clipboard-list" className="text-indigo-600" /> 등록 완료된 임직원 마스터 현황
                        </h4>
                        <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full border">
                            현재 총계: {departmentMembers.length}명
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider font-semibold border-b border-slate-200">
                                    <th className="py-3 px-4">성명 / 직책</th>
                                    <th className="py-3 px-4">소속 부서</th>
                                    <th className="py-3 px-4">1차 성과/행동</th>
                                    <th className="py-3 px-4">최종 성과/행동</th>
                                    <th className="py-3 px-4">조정 코멘트</th>
                                    <th className="py-3 px-4 text-center">작업</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {departmentMembers.map(m => (
                                    <tr key={m.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="py-3.5 px-4">
                                            <span className="font-bold text-slate-900">{m.name}</span>
                                            <span className="text-[10px] text-slate-400 block mt-0.5">
                                                {m.duty}{m.isAdmin ? ' · 시스템 관리자' : ''}
                                                {m.evalGroup ? ` · ${evalGroupLabel(m.evalGroup)}` : ''}
                                                {m.disabled ? ' · 사용중지' : ''}
                                            </span>
                                        </td>
                                        <td className="py-3.5 px-4 font-semibold text-slate-600">{m.department}</td>
                                        <td className="py-3.5 px-4">
                                            <span className="font-semibold text-slate-500">{m.perfGrade} / {m.behaviorGrade}</span>
                                        </td>
                                        <td className="py-3.5 px-4">
                                            <span className="font-extrabold text-indigo-600">{m.finalPerf} / {m.finalBehavior}</span>
                                        </td>
                                        <td className="py-3.5 px-4 text-slate-400 max-w-[150px] truncate" title={m.note}>
                                            {m.note || '-'}
                                        </td>
                                        <td className="py-3.5 px-4 text-center">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSetMemberDisabled(m.id, m.name, !m.disabled)}
                                                    className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                                                        m.disabled
                                                            ? 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                                                            : 'text-amber-700 border-amber-200 hover:bg-amber-50'
                                                    }`}
                                                    title={m.disabled ? '사용 복구' : '사용 중지'}
                                                >
                                                    {m.disabled ? '복구' : '중지'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteMember(m.id, m.name)}
                                                    className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                                                    title="사원 삭제"
                                                >
                                                    <Icon name="trash" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
